import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Glyph } from "../../components/ui";
import { iconChipStyle } from "../../lib/icons-resolve";
import { Button, Caption, DescrBox, DifficultyTag, Field, Icon, Input, Lead, List, ListRow, SectionHeader, Segmented, Sheet, Tag, Toggle } from "../../components/ui/ds";
import "./recovery-sheet.css";

// v30 — Režim prežitia. Kouč pri stave „fading" ponúkne tlačidlo, ktoré otvorí TENTO sheet;
// až on volá POST /api/recovery/start. Dve veci si volí používateľ, lebo ani jednu nevie
// appka uhádnuť za neho:
//   1. TRVANIE — kríza nemá jednu dĺžku (2 dni / týždeň / vlastné).
//   2. ČO OSTANE — predvolené sú ľahké návyky, ale kto žiadny nemá, musí vidieť a zmeniť
//      výber; inak by mu režim vypol úplne všetko a deň by ostal prázdny.
// Výzvy sa nepauzujú (sú záväzkom voči iným ľuďom) — sheet ich preto len prizná a ponúkne
// z nich ODÍSŤ, čo je viditeľný krok, nie tiché preskakovanie za chrbtom ostatných.

const CUSTOM = "custom";

function dayWord(n) {
  return n === 1 ? "deň" : n >= 2 && n <= 4 ? "dni" : "dní";
}

export function RecoverySheet({ onClose, onStarted }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);      // hodnota Segmented: číslo dní alebo CUSTOM
  const [custom, setCustom] = useState("14");
  const [keep, setKeep] = useState([]);
  const [leaving, setLeaving] = useState(null);  // id výzvy čakajúcej na potvrdenie odchodu

  useEffect(() => {
    let alive = true;
    api("/api/recovery/options").then((d) => {
      if (!alive) return;
      setData(d);
      setKeep(d.keep || []);
      setMode(d.default_days);
    }).catch(() => alive && setErr("Nepodarilo sa načítať návyky. Skús to o chvíľu."));
    return () => { alive = false; };
  }, []);

  const maxDays = data ? data.max_days : 30;
  const days = mode === CUSTOM ? Math.max(1, Math.min(maxDays, parseInt(custom, 10) || 0)) : mode;
  const customOk = mode !== CUSTOM || (parseInt(custom, 10) >= 1 && parseInt(custom, 10) <= maxDays);
  const canStart = !!data && keep.length > 0 && customOk && !busy;

  const toggleKeep = (id) =>
    setKeep((k) => (k.includes(id) ? k.filter((x) => x !== id) : [...k, id]));

  const start = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await api("/api/recovery/start", { method: "POST", body: { days, keep } });
      onStarted && onStarted(r);
    } catch (e) {
      setErr((e && e.message) || "Režim sa nepodarilo zapnúť.");
      setBusy(false);
    }
  };

  const leave = async (cid) => {
    try {
      await api(`/api/challenges/${cid}/leave`, { method: "DELETE" });
      setData((d) => ({ ...d, challenges: d.challenges.filter((c) => c.id !== cid) }));
    } catch {
      setErr("Z výzvy sa nepodarilo odísť.");
    }
    setLeaving(null);
  };

  const paused = data ? data.habits.length - keep.length : 0;
  const opts = data
    ? [...data.presets.map((n) => ({ value: n, label: n === 7 ? "Týždeň" : `${n} ${dayWord(n)}` })),
       { value: CUSTOM, label: "Vlastné" }]
    : [];

  return (
    <Sheet
      icon="heart" title="Režim prežitia"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" block onClick={onClose}>Zrušiť</Button>
          <Button variant="primary" block disabled={!canStart} onClick={start}>
            {busy ? "Zapínam…" : `Zapnúť na ${days || 0} ${dayWord(days || 0)}`}
          </Button>
        </>
      }
    >
      {!data && !err && <Caption variant="note">Načítavam…</Caption>}
      {err && <div className="bz-recovery__err">{err}</div>}

      {data && (
        <>
          {/* úvodný text patrí do TELA sheetu — v podnadpise hlavičky sa oreže */}
          <DescrBox className="bz-recovery__intro"
            text="Na pár dní odložíme, čo nevládzeš. Séria sa nepretrhne a body zatiaľ nerieš." />
          <Field label="Ako dlho">
            <Segmented options={opts} value={mode} onChange={setMode} />
          </Field>
          {mode === CUSTOM && (
            <Field label={`Počet dní (1 – ${maxDays})`} htmlFor="recovery-days">
              <Input id="recovery-days" type="number" inputMode="numeric" min={1} max={maxDays}
                value={custom} onChange={(e) => setCustom(e.target.value)} />
            </Field>
          )}

          <SectionHeader label="Čo ti ostane" count={keep.length} />
          <Caption variant="note">
            {keep.length === 0
              ? "Nechaj si aspoň jeden návyk — inak by ti deň ostal prázdny."
              : `Ostatné (${paused}) sa na ten čas odložia. Kedykoľvek to môžeš ukončiť skôr.`}
          </Caption>
          <List className="bz-recovery__list">
            {data.habits.map((h) => (
              <ListRow
                key={h.id}
                lead={<Lead size="sm" tone="outline" style={iconChipStyle(h.icon_color)}><Glyph icon={h.icon} size={17} /></Lead>}
                title={h.label}
                meta={
                  <span className="bz-recovery__meta">
                    <DifficultyTag level={h.difficulty} size="xs" showLabel={false} />
                    {h.rate != null && <Tag tone="neutral" size="xs">{h.rate} % za mesiac</Tag>}
                  </span>
                }
                trailing={
                  <Toggle on={keep.includes(h.id)} onChange={() => toggleKeep(h.id)}
                    aria-label={`Nechať návyk ${h.label}`} />
                }
              />
            ))}
          </List>

          {data.challenges.length > 0 && (
            <>
              <SectionHeader label="Výzvy bežia ďalej" count={data.challenges.length} />
              <Caption variant="note">
                Tie si sľúbila ostatným, tak ich režim nepauzuje. Ak niektorú teraz neutiahneš,
                môžeš z nej odísť.
              </Caption>
              <List className="bz-recovery__list">
                {data.challenges.map((c) => (
                  <ListRow
                    key={c.id}
                    lead={<Lead size="sm" tone="outline"><Icon name={c.is_competition ? "trophy" : "flag"} size={16} color="currentColor" /></Lead>}
                    title={c.name}
                    meta={c.habit}
                    trailing={
                      leaving === c.id ? (
                        <span className="bz-recovery__confirm">
                          <Button variant="ghost" size="sm" onClick={() => setLeaving(null)}>Späť</Button>
                          <Button variant="destructive" size="sm" onClick={() => leave(c.id)}>Naozaj odísť</Button>
                        </span>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setLeaving(c.id)}>Odísť</Button>
                      )
                    }
                  />
                ))}
              </List>
            </>
          )}
        </>
      )}
    </Sheet>
  );
}
